import { motion } from "framer-motion";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "@/hooks/useTranslation";

const CollaboratorsCarousel = () => {
  const { t } = useTranslation();
  const [isPaused, setIsPaused] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef<HTMLElement>(null);

  // Intersection Observer to pause when out of view
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        setIsVisible(entry.isIntersecting);
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  // Handle reduced motion preference
  useEffect(() => {
    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (prefersReducedMotion) {
      setIsPaused(true);
    }
  }, []);
  const collaborators = [{
    name: "Vida24",
    logo: "/lovable-uploads/fa67e24a-6404-4419-893a-c5e581791e70.png",
    description: t.collaboratorsCarousel.products.vida24
  }, {
    name: "Vida24 Healthcare",
    logo: "/lovable-uploads/5e84aeaf-64db-46d6-9063-dbbc602e9bfe.png",
    description: t.collaboratorsCarousel.products.vida24Healthcare
  }, {
    name: "Vida24 Monitoring",
    logo: "/lovable-uploads/7fbafa46-5135-4711-b0ad-2b071517233e.png",
    description: t.collaboratorsCarousel.products.vida24Monitoring
  }, {
    name: "VidAware",
    logo: "/lovable-uploads/5f497884-6abd-4a46-a1b0-ace4a569e5e1.png",
    description: t.collaboratorsCarousel.products.vidAware
  }, {
    name: "VidATrack",
    logo: "/lovable-uploads/bdb4441e-3f9a-4bf0-84fd-2a44e47dacb9.png",
    description: t.collaboratorsCarousel.products.vidATrack
  }, {
    name: "Vida24 Marine",
    logo: "/lovable-uploads/4bb7e37d-3f55-4bcb-9f8a-79bcc7ee20af.png",
    description: t.collaboratorsCarousel.products.vida24Marine
  }, {
    name: "Vida",
    logo: "/lovable-uploads/2bf3e713-3a54-4d57-af8d-aad1ce499295.png",
    description: t.collaboratorsCarousel.products.vida
  }, {
    name: "Vida24 Vet",
    logo: "/lovable-uploads/vida24-vet.png",
    description: t.collaboratorsCarousel.products.vida24Vet
  }];
  return (
    <section 
      ref={sectionRef}
      className="py-16 bg-gradient-to-b from-white to-neutral-100 overflow-hidden"
    >
      <div className="container-padding">
        <motion.div 
          initial={{ opacity: 0, y: 20 }} 
          whileInView={{ opacity: 1, y: 0 }} 
          transition={{ duration: 0.6 }} 
          viewport={{ once: true }} 
          className="text-center mb-12"
        >
          <h2 className="text-4xl font-bold text-primary mb-4 py-[10px]">
            {t.collaboratorsCarousel.title}
          </h2>
          <p className="text-lg max-w-2xl mx-auto text-[#0e2954] py-[2px]">
            {t.collaboratorsCarousel.subtitle}
          </p>
        </motion.div>

        <div className="relative overflow-hidden">
          <div 
            className={`flex w-max gap-0 whitespace-nowrap will-change-transform ${
              isPaused || !isVisible 
                ? 'animation-paused' 
                : 'animate-scroll-seamless-continuous'
            }`}
          >
            {/* First set of logos */}
            {collaborators.map((collaborator, index) => (
              <div 
                key={`first-${index}`} 
                className="flex-shrink-0 flex flex-col items-center justify-center px-20 group"
                style={{ minWidth: 'fit-content' }}
              >
                <img 
                  src={collaborator.logo} 
                  alt={collaborator.name} 
                  className="h-28 w-48 object-contain filter grayscale group-hover:grayscale-0 transition-all duration-300 opacity-70 group-hover:opacity-100" 
                />
                {collaborator.description && (
                  <p className="text-sm text-[#0e2954] mt-2 text-center font-medium opacity-50 group-hover:opacity-100 transition-opacity duration-300">
                    {collaborator.description}
                  </p>
                )}
              </div>
            ))}
            {/* Duplicate set for seamless loop */}
            {collaborators.map((collaborator, index) => (
              <div 
                key={`second-${index}`} 
                className="flex-shrink-0 flex flex-col items-center justify-center px-20 group"
                style={{ minWidth: 'fit-content' }}
              >
                <img 
                  src={collaborator.logo} 
                  alt={collaborator.name} 
                  className="h-28 w-48 object-contain filter grayscale group-hover:grayscale-0 transition-all duration-300 opacity-70 group-hover:opacity-100" 
                />
                {collaborator.description && (
                  <p className="text-sm text-[#0e2954] mt-2 text-center font-medium opacity-50 group-hover:opacity-100 transition-opacity duration-300">
                    {collaborator.description}
                  </p>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};
export default CollaboratorsCarousel;